/**
 * VerifyPage — public artifact verification verdict.
 *
 * Route: /verify/:token (PUBLIC — no RequireAuth wrapper)
 * Audience: a buyer, inspector, or any third-party scanning a QR code or
 * following a shared link from a vehicle owner.
 *
 * Calls GET /v1/verify/:token (unauthenticated). Always HTTP 200; the
 * `status` field carries the verdict. Three states:
 *
 *   verified    — sealed record confirmed; show trust signals.
 *   notVerified — honest "could not verify" (no vehicle-existence oracle).
 *   expired     — link has expired; owner can renew.
 *
 * No Firestore, no auth. Just a fetch + verdict display.
 *
 * Capacitor readiness:
 *   - No firebase/* imports.
 *   - No navigator.onLine.
 *   - No localStorage/sessionStorage for sensitive data.
 *   - No Web APIs without Capacitor plugin equivalents.
 *   - Standard https:// URL via React Router param.
 */

import { Pill } from '@/components/Pill';
import { StampChip } from '@/components/StampChip';
import { F } from '@/design/fonts';
import { RADIUS, SPACE, T } from '@/design/tokens';
import {
  ErrorPanel,
  ExpiredPanel,
  LoadingPanel,
  NotAvailablePanel,
} from '@/features/verify/shared';
import { useVerifyArtifact } from '@/hooks/useVerifyArtifact';
import type { VerifyArtifactVerified } from '@/types/api';
import { FileCheck, ShieldCheck } from 'lucide-react';
import { useParams } from 'react-router-dom';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const ARTIFACT_LABELS: Record<string, string> = {
  serviceHistory: 'Service history',
  listingBundle: 'Listing bundle',
  // 'valuationPacket' (US3/US4) is a declared-but-unimplemented artifact type
  // (gg-app rejects it — use serviceHistory). Label is facts-only per sterling:
  // "Valuation packet" money-framed a product that computes no value. Revisit
  // vs the artifact's real contents (→ "Provenance packet" if it carries the
  // ownership chain) if/when it ships.
  valuationPacket: 'Records packet',
};

/**
 * Format an ISO 8601 UTC string to a readable date.
 * Uses US English locale: "May 25, 2026".
 */
function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      timeZone: 'UTC',
    });
  } catch {
    return iso;
  }
}

/**
 * Friendly label map for known document keys returned by the full-disclosure
 * document mirror. Any key not listed here falls back to humanized Title Case.
 */
const DOCUMENT_KEY_LABELS: Record<string, string> = {
  entryCount: 'Entries',
  asOf: 'As of',
  sha256: 'Content hash',
  contentHash: 'Content hash',
  vehicleId: 'Vehicle ID',
  make: 'Make',
  model: 'Model',
  year: 'Year',
  vehicleYear: 'Vehicle Year',
  vin: 'VIN',
  chassisCode: 'Chassis',
  color: 'Color',
  engine: 'Engine',
};

/**
 * Convert a camelCase / PascalCase document key to a Title Case label.
 * Uses the DOCUMENT_KEY_LABELS map first; unknown keys are humanized.
 */
function humanizeDocumentKey(key: string): string {
  if (key in DOCUMENT_KEY_LABELS) return DOCUMENT_KEY_LABELS[key]!;
  // Split on camelCase/PascalCase boundaries and Title Case each word.
  return key
    .replace(/([A-Z])/g, ' $1')
    .trim()
    .split(/\s+/)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');
}

/**
 * Truncate a SHA-256 hex to a readable fingerprint:
 * first 8 chars + "…" + last 8 chars.
 */
function truncateSha(hex: string): string {
  if (hex.length <= 20) return hex;
  return `${hex.slice(0, 8)}…${hex.slice(-8)}`;
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function PageShell({ children }: { children: React.ReactNode }) {
  return (
    <main
      style={{
        minHeight: '100dvh',
        background: T.bg,
        padding: `${SPACE.lg}px ${SPACE.md}px`,
        fontFamily: F.body,
      }}
    >
      <div style={{ maxWidth: 480, margin: '0 auto' }}>
        {/* Brand mark — consistent with SignIn page treatment */}
        <div style={{ marginBottom: SPACE.lg }}>
          <StampChip size="md" />
        </div>
        {children}
      </div>
    </main>
  );
}

function InfoRow({ label, value, mono = false }: { label: string; value: string; mono?: boolean }) {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'baseline',
        gap: SPACE.sm,
        padding: `${SPACE.xs}px 0`,
        borderBottom: `1px solid ${T.border}`,
      }}
    >
      <span style={{ fontSize: 12, color: T.textMuted, flexShrink: 0 }}>{label}</span>
      <span
        style={{
          fontSize: 12,
          color: T.text,
          fontFamily: mono ? F.mono : F.body,
          textAlign: 'right',
          wordBreak: 'break-all',
          fontVariantNumeric: mono ? 'tabular-nums' : undefined,
        }}
      >
        {value}
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Verdict panels
// ---------------------------------------------------------------------------

function VerifiedPanel({ data }: { data: VerifyArtifactVerified }) {
  const artifactLabel = ARTIFACT_LABELS[data.artifactType] ?? data.artifactType;

  return (
    <>
      {/* Trust verdict header */}
      <div
        style={{
          border: `1px solid ${T.border}`,
          borderLeft: `3px solid ${T.green}`,
          borderRadius: RADIUS.md,
          background: T.card,
          padding: `${SPACE.md}px ${SPACE.md}px`,
          marginBottom: SPACE.md,
        }}
        role="status"
        aria-label="Verification result: verified"
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: SPACE.sm,
            marginBottom: SPACE.sm,
          }}
        >
          <ShieldCheck size={20} color={T.green} aria-hidden="true" style={{ flexShrink: 0 }} />
          <h1
            style={{
              fontFamily: F.display,
              fontSize: 20,
              fontWeight: 600,
              color: T.text,
              margin: 0,
            }}
          >
            Seal intact
          </h1>
          <Pill color="green" size="sm">
            Sealed
          </Pill>
        </div>
        <p style={{ fontSize: 13, color: T.textMuted, lineHeight: 1.6, margin: 0 }}>
          This {artifactLabel.toLowerCase()} carries a valid Absolute Pinks seal. It has not changed
          since it was sealed.
        </p>
        {/* Self-limiting disclaimer — vocabulary must stay consistent with
            PublicDossierPage's DossierFooter. TODO: consolidate into a shared
            constant when the two surfaces are refactored together. */}
        <p
          style={{
            fontSize: 12,
            color: T.textMuted,
            lineHeight: 1.6,
            margin: `${SPACE.sm}px 0 0`,
          }}
        >
          &ldquo;Sealed&rdquo; means Absolute Pinks signed this record when it was submitted, and we
          have checked that signature. It proves the record hasn&rsquo;t changed since then. It does
          not prove who wrote it, or that the work was actually done.
        </p>
      </div>

      {/* Trust signals */}
      <div
        style={{
          border: `1px solid ${T.border}`,
          borderRadius: RADIUS.md,
          background: T.card,
          padding: `${SPACE.md}px ${SPACE.md}px`,
          marginBottom: SPACE.md,
        }}
      >
        <h2
          style={{
            fontFamily: F.body,
            fontSize: 13,
            fontWeight: 700,
            color: T.text,
            margin: `0 0 ${SPACE.sm}px`,
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
          }}
        >
          Record details
        </h2>

        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <InfoRow label="Document type" value={artifactLabel} />
          <InfoRow label="Entries" value={String(data.entryCount)} mono />
          <InfoRow label="History as of" value={formatDate(data.asOf)} />
          <InfoRow label="Seal checked" value={formatDate(data.sealCheckedAt)} />
          {data.attester ? <InfoRow label="Attested by" value={data.attester.label} /> : null}
          <InfoRow label="Content fingerprint" value={truncateSha(data.contentSha256)} mono />
        </div>
      </div>

      {/* Full-disclosure document mirror — only when disclosure === 'full' */}
      {data.disclosure === 'full' && data.document ? (
        <div
          style={{
            border: `1px solid ${T.border}`,
            borderRadius: RADIUS.md,
            background: T.card,
            padding: `${SPACE.md}px ${SPACE.md}px`,
            marginBottom: SPACE.md,
          }}
          aria-label="Full document summary"
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: SPACE.sm,
              marginBottom: SPACE.sm,
            }}
          >
            <FileCheck size={14} color={T.textMuted} aria-hidden="true" />
            <h2
              style={{
                fontFamily: F.body,
                fontSize: 13,
                fontWeight: 700,
                color: T.text,
                margin: 0,
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
              }}
            >
              Document summary
            </h2>
          </div>
          {/* Render document fields as key/value rows where possible */}
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {Object.entries(data.document).map(([key, value]) => (
              <InfoRow
                key={key}
                label={humanizeDocumentKey(key)}
                value={typeof value === 'string' ? value : JSON.stringify(value)}
                mono={typeof value === 'number'}
              />
            ))}
          </div>
        </div>
      ) : null}

      <p style={{ fontSize: 11, color: T.textMuted, lineHeight: 1.5, marginTop: SPACE.sm }}>
        Verification uses keys maintained by Absolute Pinks. For independent verification, confirm
        the seal key fingerprint out-of-band.
      </p>
    </>
  );
}

// LoadingPanel, ExpiredPanel, NotAvailablePanel, ErrorPanel are imported from
// @/features/verify/shared — shared with PublicDossierPage.

// ---------------------------------------------------------------------------
// Inner page — consumes the hook (requires QueryClientProvider above it)
// ---------------------------------------------------------------------------

function VerifyPageInner() {
  const { token } = useParams<{ token: string }>();
  const { data, loading, error } = useVerifyArtifact(token ?? null);

  function renderVerdict(): React.ReactNode {
    if (!token) {
      return <ErrorPanel message="No verification token in the URL." />;
    }
    if (loading) {
      return <LoadingPanel />;
    }
    if (error) {
      return <ErrorPanel message={error.message} />;
    }
    if (!data) {
      return <LoadingPanel />;
    }

    switch (data.status) {
      case 'verified':
        return <VerifiedPanel data={data} />;
      case 'notVerified':
        return <NotAvailablePanel />;
      case 'expired':
        return <ExpiredPanel />;
      default: {
        // Exhaustiveness — treat unknown status defensively as not-available.
        const _exhaustive: never = data;
        void _exhaustive;
        return <NotAvailablePanel />;
      }
    }
  }

  return <PageShell>{renderVerdict()}</PageShell>;
}

// ---------------------------------------------------------------------------
// Public export — rendered inside the app's existing QueryClientProvider
// (src/app/providers.tsx). No extra provider wrapper needed.
// ---------------------------------------------------------------------------

export function VerifyPage() {
  return <VerifyPageInner />;
}
